import React, { useCallback, useLayoutEffect, useMemo, useState } from "react";

import { compact, difference, map } from "lodash/fp";

import EditSpeakerPopover from "@app/analytics/MeetingReport/components/EditSpeakerPopover";
import { UNKNOWN_SPEAKER } from "@app/analytics/MeetingReport/constants";
import useEditSpeakerName from "@app/analytics/MeetingReport/hooks/useEditSpeakerName";
import useSessionMetadata from "@app/analytics/MeetingReport/hooks/useSessionMetadata";
import useSuggestedSpeakerNames from "@app/analytics/MeetingReport/hooks/useSuggestedSpeakerNames";
import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import useTranscriptStore from "@app/analytics/MeetingReport/stores/transcript";
import * as Events from "@app/analytics/MeetingReport/tracking/events";
import { getApiSpeakerOperation } from "@app/analytics/MeetingReport/Transcript/utils";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

const TranscriptEditSpeaker = () => {
  const [editingSpeaker, setEditingSpeaker] = useState<{
    speakerName: string | null;
    turnId: string;
  } | null>(null);

  const {
    data: { sessionId },
  } = useSessionMetadata();
  const { updateSpeaker } = useEditSpeakerName({ sessionId });

  const { editSpeaker, inEditMode, registerOnEditSpeakerClick } = useEditStore(
    (state) => ({
      editSpeaker: state.editSpeaker,
      inEditMode: state.editingSection === "transcript",
      registerOnEditSpeakerClick: state.registerOnEditSpeakerClick,
    })
  );

  const transcriptItems = useTranscriptStore((state) => state.transcriptItems);

  const { suggestedSpeakers, isLoading: suggestionsLoading } =
    useSuggestedSpeakerNames({
      enabled: true,
      modes: [inEditMode ? "edits" : "participants", "workspace_users"],
    });

  const {
    open: popoverOpen,
    onClick: onEditSpeakerClick,
    anchorElement,
    onClose: onCloseSpeakerPopover,
  } = useAnchorElement();

  const handleOnEditSpeakerClick = useCallback(
    (
      event: React.MouseEvent<HTMLElement>,
      speakerName: string | null,
      turnId: string
    ) => {
      setEditingSpeaker({ speakerName, turnId });
      return onEditSpeakerClick(event);
    },
    [onEditSpeakerClick]
  );

  const suggestedSpeakerNames = compact(map("name", suggestedSpeakers));

  const uniqueSuggestedNames = useMemo(
    () =>
      difference(suggestedSpeakerNames, compact([editingSpeaker?.speakerName])),
    [suggestedSpeakerNames, editingSpeaker?.speakerName]
  );

  useLayoutEffect(
    () => registerOnEditSpeakerClick(handleOnEditSpeakerClick),
    [handleOnEditSpeakerClick]
  );

  const handleEditSpeaker = (
    existingName: string | null,
    newName: string | null,
    action: "replace" | "update" | "untag"
  ) => {
    // The turnId is omitted when the action should apply to all speaker turns for a given speaker
    const turnId = action === "update" ? null : editingSpeaker?.turnId ?? null;

    if (inEditMode) {
      editSpeaker({
        type: "speaker",
        before: { speaker: existingName, turnId },
        after: { speaker: newName, turnId },
      });
    } else {
      Events.editSpeaker({ source: "avatar" });
      const operation = getApiSpeakerOperation({
        turnId,
        action,
        newName,
        existingName: existingName ?? UNKNOWN_SPEAKER,
      });

      updateSpeaker.mutate({ operation });
    }
  };

  return (
    <EditSpeakerPopover
      open={popoverOpen}
      onClose={onCloseSpeakerPopover}
      anchorEl={anchorElement}
      onEditSpeaker={handleEditSpeaker}
      editLoading={updateSpeaker.isPending}
      existingName={editingSpeaker?.speakerName ?? null}
      suggestions={uniqueSuggestedNames}
      suggestionsLoading={suggestionsLoading}
      transcriptItems={transcriptItems}
    />
  );
};

export default TranscriptEditSpeaker;
